import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Skeleton,
} from "@repo/ui";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Inbox } from "lucide-react";
import { formatRelativeTime } from "@repo/utils";
import type { ActivityTransaction } from "@/lib/types/activity";
import {
  getTransactionIcon,
  getTransactionColor,
} from "@/lib/utils/transaction-helpers";

interface DashboardRecentActivityProps {
  transactions: ActivityTransaction[];
  isLoading: boolean;
}

function ActivitySkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

export function DashboardRecentActivity({
  transactions,
  isLoading,
}: DashboardRecentActivityProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
        <Link to="/activity">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1"
          >
            View All
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ActivitySkeleton />
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="rounded-full bg-muted p-3 mb-3">
              <Inbox className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-1">
            {transactions.map((txn) => {
              const colorClasses = getTransactionColor(txn.transaction_type);
              const isReceipt = txn.transaction_type === "receipt";
              const quantityPrefix = isReceipt ? "+" : "-";

              return (
                <div
                  key={txn.transaction_id}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/40"
                >
                  {/* Type icon */}
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-lg border ${colorClasses}`}
                  >
                    {getTransactionIcon(txn.transaction_type)}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate leading-tight">
                      {txn.item_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs text-muted-foreground capitalize">
                        {txn.transaction_type}
                      </span>
                      {txn.created_by_name && (
                        <>
                          <span className="text-muted-foreground/40">·</span>
                          <span className="text-xs text-muted-foreground">
                            {txn.created_by_name}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Quantity */}
                  <Badge
                    variant="outline"
                    className={`shrink-0 text-[11px] h-5 px-1.5 tabular-nums font-medium ${
                      isReceipt
                        ? "text-emerald-600 border-emerald-200"
                        : txn.transaction_type === "dispense"
                          ? "text-foreground border-border"
                          : "text-amber-600 border-amber-200"
                    }`}
                  >
                    {quantityPrefix}
                    {Math.abs(txn.quantity)}
                  </Badge>

                  {/* Time */}
                  <span className="text-[11px] text-muted-foreground shrink-0 w-16 text-right">
                    {formatRelativeTime(txn.transaction_date)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
